import { Fragment, memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { createSelector } from '@reduxjs/toolkit';
import { FlashList, MasonryFlashList } from '@shopify/flash-list';
import { COLOR, SPACING } from 'constants/design-system';

import colorsConfig from 'config/colors';
import { useGridViewContext } from 'modules/GridView/contexts/GridViewContext';
import { useOrderItems } from 'modules/GridView/utils/useOrderItems';
import { useOrders } from 'modules/GridView/utils/useOrders';
import { TAB_BARS_LIST } from 'store/App/constants';
import { useAppSelector } from 'store/hooks';
import { OrderSelectors } from 'store/Orders';
import { Order } from 'store/Orders/types';
import { SettingSelectors } from 'store/Settings';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { usePullToRefreshOrder } from 'util/hooks';
import { useHaveHiddenItems } from 'util/hooks/useHaveHiddenItems';
import { useNavigationContext } from 'util/hooks/useNavigationContext';
import { Text } from 'components';
import { OrderDetailCardForGrid } from 'components/OrderDetailCard/OrderDetailCardForGrid';

import type { ReactNode } from 'react';
import type { TabBarKey } from 'store/App/constants';

type ItemProps = {
  order: Order;
  tabBarKey?: TabBarKey;
};

export const Item = memo((props: ItemProps) => {
  const { order, tabBarKey } = props;
  const { items = [], allItems, filteredItems } = useOrderItems(order, false);

  const cardViewLayoutLayout = useAppSelector(
    SettingSelectors.selectCardViewLayoutLayout,
  );

  const { setSelectedOrder, hiddenItemDisclosure, setSelectedOrderItemUuid } =
    useGridViewContext();

  const { hiddenItemsCount } = useHaveHiddenItems(
    allItems ?? [],
    filteredItems ?? [],
  );

  return (
    <OrderDetailCardForGrid
      order={order}
      items={items}
      shouldIncludeGhostMarginForScheduledForHead={
        cardViewLayoutLayout === 'default'
      }
      onPress={() => {
        setSelectedOrder({ uuid: order.uuid, tab: tabBarKey });
      }}
      onPressOrderItem={(itemUuid) => {
        setSelectedOrder({ uuid: order.uuid, tab: tabBarKey });
        setSelectedOrderItemUuid(itemUuid);
      }}
      hiddenItemsCount={hiddenItemsCount}
      onPressShowHiddenItem={() => {
        setSelectedOrder({ uuid: order.uuid, tab: tabBarKey });
        hiddenItemDisclosure.onOpen();
      }}
    />
  );
});

export const selectCardViewSettings = createSelector(
  [
    SettingSelectors.selectCardViewLayoutLayout,
    SettingSelectors.selectCardViewLayoutStatusTab,
    SettingSelectors.selectCardViewLayoutAllTabColumn,
    SettingSelectors.selectCardViewLayoutNewTabColumn,
    SettingSelectors.selectCardViewLayoutCookingTabColumn,
  ],
  (layout, statusTab, allTabColumn, newTabColumn, cookingTabColumn) => ({
    layout,
    statusTab,
    allTabColumn,
    newTabColumn,
    cookingTabColumn,
  }),
);

export const List = () => {
  const { navigationState, activeRouteKey } = useNavigationContext();

  const logEvent = useLogEventCallback();

  useUpdateEffect(() => {
    logEvent(
      AnalyticsEvents.tabChangedInOverviewScreen({
        tab: TAB_BARS_LIST[navigationState.index]?.value,
      }),
    );
  }, [navigationState.index]);

  const filteredOrder = useOrders();

  const [isFetching, onRefresh] = usePullToRefreshOrder();

  const renderItem = useCallback(
    (tabBarKey?: TabBarKey) =>
      ({ item }: { item: Order }) => (
        <View style={{ padding: SPACING.theme[6], width: '100%' }}>
          <Item order={item} tabBarKey={tabBarKey} />
        </View>
      ),
    [activeRouteKey],
  );

  const keyExtractor = useCallback((item: Order) => item.uuid, []);

  const { layout, statusTab, allTabColumn, newTabColumn, cookingTabColumn } =
    useAppSelector(selectCardViewSettings);

  const DynamicFlashList = useMemo(
    () => (layout === 'compact' ? MasonryFlashList : FlashList),
    [layout],
  );

  const safeAreaInsets = useSafeAreaInsets();
  return (
    <View style={{ backgroundColor: colorsConfig.lightBlue2, height: '100%' }}>
      <View
        style={{
          flexGrow: 1,
          flexShrink: 1,
          padding: SPACING.theme[10],
          paddingHorizontal: SPACING.theme[10],
          marginTop: safeAreaInsets.top,
        }}
      >
        {statusTab === 'split' && activeRouteKey === 'newAndCooking' ? (
          <View
            style={{
              flexDirection: 'row',
              gap: SPACING.theme[12],
              height: '100%',
            }}
          >
            <NewAndCookingStateContainer>
              {({ cookingOrders, newOrders }) =>
                [
                  {
                    order: newOrders,
                    columnCount: newTabColumn,
                    tabBarKey: 'new' as const satisfies TabBarKey,
                  },
                  {
                    order: cookingOrders,
                    columnCount: cookingTabColumn,
                    tabBarKey: 'cooking' as const satisfies TabBarKey,
                  },
                ].map((data, index) => {
                  return (
                    <Fragment key={index}>
                      {index !== 0 && (
                        <View
                          style={{
                            borderColor: COLOR.theme.white,
                            borderStyle: 'dashed',
                            borderWidth: 1,
                          }}
                        />
                      )}
                      <DynamicFlashList
                        key={`split-${index}-${newTabColumn}-${cookingTabColumn}`}
                        estimatedItemSize={600}
                        data={data.order}
                        numColumns={data.columnCount}
                        onRefresh={onRefresh}
                        refreshing={isFetching}
                        renderItem={renderItem(data.tabBarKey)}
                        keyExtractor={keyExtractor}
                      />
                    </Fragment>
                  );
                })
              }
            </NewAndCookingStateContainer>
          </View>
        ) : (
          <DynamicFlashList
            key={`classic-${
              statusTab === 'split'
                ? (newTabColumn ?? 0) + (cookingTabColumn ?? 0)
                : allTabColumn
            }`}
            estimatedItemSize={600}
            data={filteredOrder}
            numColumns={
              statusTab === 'split'
                ? (newTabColumn ?? 0) + (cookingTabColumn ?? 0)
                : allTabColumn
            }
            onRefresh={onRefresh}
            refreshing={isFetching}
            renderItem={renderItem()}
            keyExtractor={keyExtractor}
          />
        )}
      </View>
    </View>
  );
};

type NewAndCookingStateContainerProps = {
  children?: (value: {
    newOrders: Order[];
    cookingOrders: Order[];
  }) => ReactNode;
};
const NewAndCookingStateContainer = (
  props: NewAndCookingStateContainerProps,
) => {
  const { children } = props;

  const cookingOrders = useAppSelector(
    OrderSelectors.selectBeingPreparedItemsAsOrderFiltered,
  );
  const newOrders = useAppSelector(
    OrderSelectors.selectNotBeingPreparedItemsAsOrderFiltered,
  );

  return children?.({ cookingOrders, newOrders });
};
